/**
 * Normalised Graph error (§3.4). One shape the whole app reasons about, so a
 * Graph-side change is a contained fix here rather than scattered across features.
 */
export interface NormalizedGraphError {
  readonly status: number;
  readonly code: string;
  readonly message: string;
  readonly isThrottled: boolean;      // 429 — back off (§6)
  readonly isForbidden: boolean;      // 403 — often licence/tenant-gated (e.g. C5)
  readonly isConsentRequired: boolean; // needs interactive consent for a new scope
  readonly retryAfterSeconds?: number;
  readonly raw?: unknown;
}

interface GraphClientErrorLike {
  statusCode?: number;
  code?: string;
  message?: string;
  headers?: { get?: (k: string) => string | null } | Record<string, string>;
  body?: string;
}

export function normalizeGraphError(err: unknown): NormalizedGraphError {
  const e = (err ?? {}) as GraphClientErrorLike;
  const status = typeof e.statusCode === 'number' ? e.statusCode : 0;
  const code = e.code ?? 'unknownError';
  const message = e.message ?? 'Unknown Graph error';

  let retryAfterSeconds: number | undefined;
  const h = e.headers;
  const raw =
    h && typeof (h as { get?: unknown }).get === 'function'
      ? (h as { get: (k: string) => string | null }).get('Retry-After')
      : (h as Record<string, string> | undefined)?.['Retry-After'];
  if (raw != null) {
    const n = Number(raw);
    if (!Number.isNaN(n)) retryAfterSeconds = n;
  }

  const consentCodes = new Set([
    'consent_required',
    'interaction_required',
    'invalid_grant',
    'AADSTS65001',
  ]);

  return {
    status,
    code,
    message,
    isThrottled: status === 429,
    isForbidden: status === 403,
    isConsentRequired: consentCodes.has(code) || status === 401,
    retryAfterSeconds,
    raw: err,
  };
}

export function isNormalizedGraphError(x: unknown): x is NormalizedGraphError {
  return (
    typeof x === 'object' &&
    x !== null &&
    'isThrottled' in x &&
    'status' in x
  );
}
